import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { callAPI } from "../../functions/api";
import { styled } from "@mui/material/styles";

const MarkdownContent = styled(Box)(({ theme }) => {
  const isLight = theme.palette.mode === "light";
  return {
    color: isLight ? "#333" : "#eee",
    fontSize: "15px",
    lineHeight: 1.6,
    "& h1": {
      fontSize: "22px",
      color: isLight ? "#111" : "#fff",
      marginTop: "20px",
      marginBottom: "10px",
    },
    "& h2": {
      fontSize: "20px",
      color: theme.palette.primary.main,
      marginTop: "18px",
      marginBottom: "8px",
    },
    "& h3": {
      fontSize: "18px",
      color: theme.palette.primary.main,
      marginTop: "16px",
      marginBottom: "6px",
    },
    "& strong": {
      color: isLight ? "#111" : "#fff",
      fontWeight: 600,
    },
    "& code": {
      backgroundColor: isLight ? "#eee" : "#444",
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "0.95em",
      color: theme.palette.primary.main,
    },
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "20px",
      backgroundColor: isLight ? "#f9f9f9" : "#1E1E1E",
    },
    "& th, & td": {
      border: `1px solid ${isLight ? "#ccc" : "#666"}`,
      padding: "8px",
      textAlign: "left",
    },
    "& th": {
      backgroundColor: isLight ? "#ddd" : "#2a2a2a",
      color: theme.palette.primary.main,
    },
  };
});

const AIInsightsModal = ({ open, onClose, data, prompt, keyValue }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const [insights, setInsights] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generate = async () => {
      setLoading(true);
      try {
        const response = await callAPI("ai/insights/generate", "POST", {
          DataObject: data,
          Prompt: prompt,
          CacheKey: keyValue,
        });

        if (!response.ok) throw new Error("Failed to generate insights");

        const result = await response.json();
        const markdown = result?.value?.value;
        if (typeof markdown !== "string") throw new Error("Invalid markdown format");

        setInsights(markdown);
      } catch (error) {
        console.error("Insight generation failed:", error);
        setInsights("❌ An error occurred while generating insights.");
      } finally {
        setLoading(false);
      }
    };

    if (open) generate();
  }, [open, data, prompt, keyValue]);

  const styles = {
    modalContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      backgroundColor: isLight ? "rgba(255,255,255,0.75)" : "rgba(0, 0, 0, 0.6)",
    },
    modalPaper: {
      width: "90%",
      maxWidth: "1100px",
      backgroundColor: isLight ? "#fff" : "#303030",
      color: isLight ? "#000" : "#fff",
      borderRadius: "16px",
      boxShadow: isLight ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0,0,0,0.4)",
      maxHeight: "85%",
      display: "flex",
      flexDirection: "column",
      paddingBottom: "10px",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "20px",
      position: "sticky",
      top: 0,
      borderRadius: "16px",
      backgroundColor: isLight ? "#f5f5f5" : "#303030",
      zIndex: 10,
    },
    contentBox: {
      padding: "20px",
      overflowY: "auto",
      maxHeight: "70vh",
    },
    loadingBox: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      paddingTop: "60px",
    },
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.modalContainer}>
        <Paper sx={styles.modalPaper}>
          <Box sx={styles.modalHeader}>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: theme.palette.warning.main }}
              >
                🧠 Insights by CORA
              </Typography>
              <Typography sx={{ fontSize: "10px", marginLeft: "35px" }}>
                Cloud Operations Reporting AI
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: isLight ? "#000" : "#fff" }} />
            </IconButton>
          </Box>

          <Divider sx={{ backgroundColor: isLight ? "#ccc" : "#555" }} />

          <Box sx={styles.contentBox}>
            {loading ? (
              <Box sx={styles.loadingBox}>
                <CircularProgress color="inherit" />
                <Typography sx={{ color: isLight ? "#666" : "#ccc", mt: 2 }}>
                  Generating insights, please wait...
                </Typography>
              </Box>
            ) : (
              <MarkdownContent>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{insights}</ReactMarkdown>
              </MarkdownContent>
            )}
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default AIInsightsModal;
